/**
 * DOB Step Validator
 *
 * Targeted validation for DOB fields (dob, coapp_dob) that disables
 * the Next button only when these fields are visible and invalid.
 *
 * Uses event delegation to handle dynamically rendered form steps.
 */

import { FormatValidators } from './format-validators';
import { FieldType } from '../detection-patterns';

/**
 * DOB field names to validate
 */
const DOB_FIELD_NAMES = ['dob', 'coapp_dob'];

/**
 * Next button selector
 */
const NEXT_BUTTON_SELECTOR = 'input[data-form="next-btn"]';

/**
 * DOB Step Validator class - uses event delegation for multi-step forms
 */
export class DobStepValidator {
  private isInitialized = false;
  private validationState: Map<string, boolean> = new Map();
  private lastButtonState: boolean | null = null;

  /**
   * Initialize the DOB step validator with event delegation
   */
  public initialize(): void {
    if (this.isInitialized) {
      console.log('🎂 DOB Step Validator already initialized');
      return;
    }

    console.log('🎂 DOB Step Validator initializing with event delegation...');

    // Use event delegation - listen on document for all input events
    document.addEventListener('input', (event) => {
      const target = event.target as HTMLInputElement;
      if (this.isDobField(target)) {
        console.log(`🎂 Input event on DOB field: ${target.getAttribute('data-name') || target.name}`);
        this.handleDobInput(target);
      }
    });

    document.addEventListener('blur', (event) => {
      const target = event.target as HTMLInputElement;
      if (this.isDobField(target)) {
        console.log(`🎂 Blur event on DOB field: ${target.getAttribute('data-name') || target.name}`);
        this.handleDobInput(target);
      }
    }, true); // Use capture for blur

    document.addEventListener('focus', (event) => {
      const target = event.target as HTMLInputElement;
      if (this.isDobField(target)) {
        console.log(`🎂 Focus event on DOB field: ${target.getAttribute('data-name') || target.name}`);
        this.handleDobInput(target);
      }
    }, true); // Use capture for focus

    this.isInitialized = true;
    console.log('🎂 DOB Step Validator initialized with event delegation');
  }

  /**
   * Check if an element is a DOB field
   */
  private isDobField(element: HTMLInputElement): boolean {
    if (!element || element.tagName !== 'INPUT') return false;

    const dataName = element.getAttribute('data-name');
    const name = element.name;
    const id = element.id;

    return DOB_FIELD_NAMES.includes(dataName || '') ||
           DOB_FIELD_NAMES.includes(name || '') ||
           DOB_FIELD_NAMES.includes(id || '');
  }

  /**
   * Handle input on a DOB field
   */
  private handleDobInput(field: HTMLInputElement): void {
    const fieldId = field.getAttribute('data-name') || field.name || field.id;
    const value = field.value.trim();

    // Validate the field
    let isValid = false;
    if (value) {
      const result = FormatValidators.validateDate(value, FieldType.DATE_OF_BIRTH);
      isValid = result.isValid;
    }

    this.validationState.set(fieldId, isValid);
    console.log(`🎂 DOB "${fieldId}" value="${value}" → ${isValid ? 'VALID' : 'INVALID'}`);

    // Update button state
    this.updateNextButtonState(field);
  }

  /**
   * Check if a field is visible
   */
  private isFieldVisible(field: HTMLElement): boolean {
    if (!field || !document.body.contains(field)) return false;

    const rect = field.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return false;

    const style = window.getComputedStyle(field);
    if (style.display === 'none' || style.visibility === 'hidden') return false;

    if (field.offsetParent === null && style.position !== 'fixed') return false;

    return true;
  }

  /**
   * Find all visible DOB fields on the page
   */
  private findVisibleDobFields(): HTMLInputElement[] {
    const fields: HTMLInputElement[] = [];

    for (const fieldName of DOB_FIELD_NAMES) {
      // Try data-name, name, and id
      const selectors = [
        `input[data-name="${fieldName}"]`,
        `input[name="${fieldName}"]`,
        `input#${fieldName}`
      ];

      for (const selector of selectors) {
        const field = document.querySelector(selector) as HTMLInputElement;
        if (field && this.isFieldVisible(field) && !fields.includes(field)) {
          fields.push(field);
          break; // Found this field, move to next fieldName
        }
      }
    }

    return fields;
  }

  /**
   * Find the Next button near a DOB field
   */
  private findNextButton(dobField: HTMLInputElement): HTMLInputElement | null {
    // Walk up to find a container with the Next button
    let container: HTMLElement | null = dobField.closest('form') ||
                                          dobField.closest('[data-form-step]') ||
                                          dobField.closest('.w-form');

    if (!container) {
      container = dobField.parentElement;
      for (let i = 0; i < 10 && container; i++) {
        if (container.querySelector(NEXT_BUTTON_SELECTOR)) break;
        container = container.parentElement;
      }
    }

    if (!container) return null;

    const button = container.querySelector(NEXT_BUTTON_SELECTOR) as HTMLInputElement;
    return button && this.isFieldVisible(button) ? button : null;
  }

  /**
   * Update the Next button state
   */
  private updateNextButtonState(triggeringField: HTMLInputElement): void {
    // Find all visible DOB fields
    const visibleDobFields = this.findVisibleDobFields();

    if (visibleDobFields.length === 0) {
      console.log('🎂 No visible DOB fields found');
      return;
    }

    console.log(`🎂 Found ${visibleDobFields.length} visible DOB field(s)`);

    // Find the Next button
    const nextButton = this.findNextButton(triggeringField);
    if (!nextButton) {
      console.log('🎂 Next button not found');
      return;
    }

    // Check if any visible DOB field is invalid
    let hasInvalidField = false;
    for (const field of visibleDobFields) {
      const fieldId = field.getAttribute('data-name') || field.name || field.id;
      const value = field.value.trim();

      // Re-validate to ensure current state
      let isValid = false;
      if (value) {
        const result = FormatValidators.validateDate(value, FieldType.DATE_OF_BIRTH);
        isValid = result.isValid;
      }

      console.log(`🎂 Field "${fieldId}" value="${value}" valid=${isValid}`);

      if (!isValid) {
        hasInvalidField = true;
      }
    }

    console.log(`🎂 hasInvalidField=${hasInvalidField} lastButtonState=${this.lastButtonState}`);

    // Apply state immediately
    if (hasInvalidField) {
      console.log('🎂 DISABLING Next button (immediate)');
      this.disableNextButton(nextButton);
    } else {
      console.log('🎂 ENABLING Next button (immediate)');
      this.enableNextButton(nextButton);
    }

    // Also apply after a short delay to override Webflow's scripts
    const btn = nextButton; // Capture reference
    const shouldDisable = hasInvalidField;
    setTimeout(() => {
      if (shouldDisable) {
        console.log('🎂 DISABLING Next button (delayed)');
        this.disableNextButton(btn);
      }
    }, 50);

    this.lastButtonState = hasInvalidField;
  }

  /**
   * Disable the Next button using Webflow's pattern
   */
  private disableNextButton(button: HTMLInputElement): void {
    button.classList.add('disabled');
    button.style.opacity = '0.4';
    button.style.pointerEvents = 'none';
  }

  /**
   * Enable the Next button using Webflow's pattern
   */
  private enableNextButton(button: HTMLInputElement): void {
    button.classList.remove('disabled');
    button.style.opacity = '1';
    button.style.pointerEvents = 'auto';
  }
}

/**
 * Singleton instance
 */
let dobStepValidatorInstance: DobStepValidator | null = null;

/**
 * Get or create the DOB step validator instance
 */
export function getDobStepValidator(): DobStepValidator {
  if (!dobStepValidatorInstance) {
    dobStepValidatorInstance = new DobStepValidator();
  }
  return dobStepValidatorInstance;
}

/**
 * Initialize the DOB step validator
 */
export function initializeDobStepValidator(): void {
  const validator = getDobStepValidator();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => validator.initialize());
  } else {
    validator.initialize();
  }
}
